import { ChatResponse, ClarifyingQuestionsResponse } from "../../components/Deepchat/models";
import { SearchFilters } from "../../models/search";

// A clarification turn only lives in the in-memory session atom and is never persisted by the
// backend, so a mid-clarification page refresh would otherwise drop the whole turn (blank page).
// We snapshot just enough of the un-answered turn to rebuild it on load; the user's in-progress
// selections are restored separately from the form's own draft (CLARIFY_STORAGE_KEY).
const CLARIFY_TURN_STORAGE_KEY = "deepchat-clarify-turn";

export interface PersistedClarifyTurn {
  userChatSession: string;
  userQuery: string;
  searchObject?: SearchFilters;
  clarification: ClarifyingQuestionsResponse;
}

export function saveClarifyTurn(turn: PersistedClarifyTurn): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CLARIFY_TURN_STORAGE_KEY, JSON.stringify(turn));
  } catch {
    // Best-effort; a failure here just means refresh won't restore the turn.
  }
}

export function readClarifyTurn(): PersistedClarifyTurn | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CLARIFY_TURN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedClarifyTurn;
    if (!parsed?.userChatSession || !parsed?.clarification) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearClarifyTurn(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CLARIFY_TURN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Rebuild the un-answered clarification turn as a ChatResponse so SessionThread can render the
// module (and the form re-hydrates the user's draft selections on mount).
export function clarifyTurnToResponse(turn: PersistedClarifyTurn): ChatResponse {
  return {
    user_query: turn.userQuery,
    user_id: "",
    user_chat_session: turn.userChatSession as ChatResponse["user_chat_session"],
    search_object: turn.searchObject,
    sources: [],
    clarification: turn.clarification,
  };
}
